/* ============================================================================
	Design Tokens — ByPonto
	----------------------------------------------------------------------------
	Fonte única de verdade da identidade visual. Espelhado em:
	  - src/sass/_tokens.scss  (CSS custom properties + variáveis SCSS)
	  - src/less/_vars.less    (variáveis do tema Ant Design v4)

	Ao alterar um valor aqui, replique no arquivo correspondente.
============================================================================ */

/* ----------------------------------------------------------------------------
	Cores
---------------------------------------------------------------------------- */
export const palette = {
	// Sidebar / superfícies escuras
	navy900: "#0D1B2A",
	navy800: "#122539",
	navy700: "#1B3049",
	navy600: "#24405E",

	// Azul primário
	blue700: "#1D4ED8",
	blue600: "#2563EB",
	blue500: "#3B82F6",
	blue400: "#60A5FA",
	blue200: "#BFDBFE",
	blue100: "#DBEAFE",
	blue50: "#EFF6FF",

	// Ciano accent
	cyan700: "#0E7490",
	cyan600: "#0891B2",
	cyan500: "#06B6D4",
	cyan400: "#22D3EE",
	cyan100: "#CFFAFE",
	cyan50: "#ECFEFF",

	// Neutros
	white: "#FFFFFF",
	neutral50: "#F8FAFC",
	neutral100: "#F1F5F9",
	neutral200: "#E2E8F0",
	neutral300: "#CBD5E1",
	neutral400: "#94A3B8",
	neutral500: "#64748B",
	neutral600: "#475569",
	neutral700: "#334155",
	neutral800: "#1E293B",
	neutral900: "#0F172A",
	black: "#000000",

	// Semânticos
	green600: "#059669",
	green500: "#10B981",
	green100: "#D1FAE5",
	amber600: "#D97706",
	amber500: "#F59E0B",
	amber100: "#FEF3C7",
	red600: "#DC2626",
	red500: "#EF4444",
	red100: "#FEE2E2",
	violet500: "#8B5CF6",
	violet100: "#EDE9FE",
	pink500: "#EC4899",
	pink100: "#FCE7F3",

	// Magenta Compose — texto do item selecionado na sidebar
	magentaCompose: "#D62A85",
};

export const colors = {
	primary: palette.blue600,
	primaryHover: palette.blue500,
	primaryActive: palette.blue700,
	accent: palette.cyan500,
	accentHover: palette.cyan400,

	success: palette.green500,
	warning: palette.amber500,
	error: palette.red500,
	info: palette.blue600,

	textPrimary: palette.neutral800,
	textSecondary: palette.neutral500,
	textDisabled: palette.neutral400,
	textInverse: palette.white,

	border: palette.neutral200,
	borderSplit: palette.neutral100,

	bodyBackground: palette.neutral50,
	componentBackground: palette.white,

	sidebarBackground: palette.navy900,
	sidebarBackgroundHover: palette.navy700,
	sidebarSubmenuBackground: palette.navy800,
	sidebarText: "rgba(255, 255, 255, 0.72)",
	sidebarTextHover: palette.white,
	sidebarTextActive: palette.white,
	sidebarBorder: "rgba(255, 255, 255, 0.08)",
	// Superfície acinzentada: grupo aberto e item selecionado
	sidebarSurface: "rgba(255, 255, 255, 0.06)",
	sidebarSurfaceHover: "rgba(255, 255, 255, 0.10)",
	sidebarItemActiveText: palette.magentaCompose,
	// Ícones da sidebar — tom único
	sidebarIcon: palette.blue400,
	sidebarIconBackground: "rgba(37, 99, 235, 0.16)",
};

/* Tons usados por StatCard/StatusBadge e gráficos — ícone + fundo suave */
export const tones = {
	primary: { color: palette.blue600, background: palette.blue100 },
	accent: { color: palette.cyan600, background: palette.cyan100 },
	success: { color: palette.green600, background: palette.green100 },
	warning: { color: palette.amber600, background: palette.amber100 },
	error: { color: palette.red600, background: palette.red100 },
	violet: { color: palette.violet500, background: palette.violet100 },
	pink: { color: palette.pink500, background: palette.pink100 },
	neutral: { color: palette.neutral600, background: palette.neutral100 },
};

/* ----------------------------------------------------------------------------
	Tipografia
---------------------------------------------------------------------------- */
export const typography = {
	fontFamily: '"Roboto Flex", roboto, Helvetica, Arial, sans-serif',
	fontFamilyMono: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace",
	fontSizeXs: 12,
	fontSizeSm: 13,
	fontSizeBase: 14,
	fontSizeLg: 16,
	fontSizeXl: 20,
	fontSizeDisplay: 28,
	lineHeightBase: 1.5715,
	lineHeightTight: 1.3,
	fontWeightRegular: 400,
	fontWeightMedium: 500,
	fontWeightSemibold: 600,
	fontWeightBold: 700,
	letterSpacingCaps: "0.08em",
};

/* ----------------------------------------------------------------------------
	Border radius
---------------------------------------------------------------------------- */
export const radius = {
	xs: 4,
	sm: 6,
	base: 8,
	md: 10,
	lg: 12,
	xl: 16,
	pill: 999,
};

/* ----------------------------------------------------------------------------
	Sombras
---------------------------------------------------------------------------- */
export const shadows = {
	xs: "0 1px 2px rgba(15, 23, 42, 0.06)",
	sm: "0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)",
	base: "0 4px 12px rgba(15, 23, 42, 0.08)",
	md: "0 6px 20px rgba(15, 23, 42, 0.10)",
	lg: "0 12px 32px rgba(15, 23, 42, 0.12)",
	focus: "0 0 0 3px rgba(37, 99, 235, 0.20)",
};

/* ----------------------------------------------------------------------------
	Espaçamento e layout
---------------------------------------------------------------------------- */
export const spacing = {
	xxs: 4,
	xs: 8,
	sm: 12,
	md: 16,
	lg: 24,
	xl: 32,
	xxl: 48,
};

export const layout = {
	headerHeight: 64,
	sidebarWidth: 265,
	sidebarCollapsedWidth: 96,
	controlHeight: 40,
};

/* ----------------------------------------------------------------------------
	Configuração global do Ant Design (ConfigProvider)
---------------------------------------------------------------------------- */

/* Tokens de cor semântica. O ConfigProvider do antd v4 aceita estes cinco
   valores; a paleta completa (tipografia, radius e sombras) é compilada a
   partir de src/less/_vars.less, que espelha os tokens deste arquivo. */
export const antdTheme = {
	primaryColor: colors.primary,
	infoColor: colors.info,
	successColor: colors.success,
	warningColor: colors.warning,
	errorColor: colors.error,
};

const theme = {
	palette,
	colors,
	tones,
	typography,
	radius,
	shadows,
	spacing,
	layout,
	antdTheme,
};

export default theme;
